import { useEffect, useState,useRef } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import {
  CommonLayout,
  NavBar,
  headerNavigation,
} from '@marketplace/common';
import { Logo, Icon, Button, Dialog } from '@marketplace/ui-elements';
import {
  useAPIFetchApplication,
  useAPIActivateDeactivateApplication,
  useAPIRegisterApplication,
  useAPIPublishApplication,
  useAPISubmitToPublish,
} from '@marketplace/api';
import { IApplicationFormatted } from '../types';
import {
  ROUTE_DEVELOPER_DASHBOARD,
  ROUTE_DEVELOPER_DASHBOARD_MY_PROFILE,
} from '@developer/constants';

import { AppStatus, AppDetails } from './components';
import { useHeaderMenu } from '../shared';

import classes from './app-page.module.scss';
import { Completion } from './components/completion';
import { ConfirmModal } from './components/confirm-modal';

type TParamsType = {
  id: string;
};

const Title = ({ link, text }: { text: string; link: string }) => {
  return (
    <div className={classes.breadcrumos_wrapper}>
      <Link to={ROUTE_DEVELOPER_DASHBOARD} className={classes.breadcrump_link}>
        {link}
      </Link>
      <Icon glyph={Icon.glyphs.font['pi-angle-right']} />
      <span className={classes.breadcrump_text}>{text}</span>
    </div>
  );
};

export const AppPage = () => {
  const { formatMessage } = useIntl();
  const { id } = useParams<TParamsType>();
  const { data, loaded } = useAPIFetchApplication(id);
  const [application, setApplication] = useState<IApplicationFormatted | any>(
    {}
  );
  const {
    data: activateDeactivateApplicationResponse,
    loaded: activateDeactivateStatusLoaded,
    loading: updatedStatusLoading,
    deactivateApplication,
    activateApplication,
  } = useAPIActivateDeactivateApplication();

  const {
    publishApplication,
    loaded: publishingLoaded,
    data: publishApplicationResponse,
  } = useAPIPublishApplication();

  const {
    submitToPublish,
    loaded:submittingloaded,
    data: submitToPublishResponse,
  } = useAPISubmitToPublish();

  const isCreated = application.status === 'CREATED';
  const isRegistered = application.status === 'REGISTERED';
  const isDeactivated =
    application?.status === 'DEACTIVATED' ||
    activateDeactivateApplicationResponse?.status === 'DEACTIVATED';
  const isPublished =
    application?.status === 'PUBLISHED' ||
    application?.status === 'DEACTIVATED';
  const isReadyToPublish= application.status==="READYTOPUBLISH";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    header: '',
    text: '',
    confirmButton: '',
  });
  const [headerButtonLabel, setHeaderButtonLabel] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const toast = useRef<any>();
  useEffect(() => {
    if (isCreated) {
      setHeaderButtonLabel(
        formatMessage({ id: 'app_page_app_register_title' })
      );
    }
    if (isRegistered) {
      setHeaderButtonLabel(formatMessage({ id: 'app_page_app_submit_to_publish_title' }));
    }
    if (isPublished) {
      setHeaderButtonLabel(activateDeactivateLabel);
    }
    if(isReadyToPublish){
      setHeaderButtonLabel(formatMessage({ id: 'app_page_app_submit_to_publish_title' }));
      setIsHeaderButtonDisabled(true);
    }
  }, [loaded, activateDeactivateApplicationResponse, application]);

  const {
    registerApplication,
    data: registerApplicationResponse,
    loaded: isRegisterLoaded,
  } = useAPIRegisterApplication();

  const activateDeactivateLabel = isDeactivated
    ? formatMessage({ id: 'app_page_app_activate_title' })
    : formatMessage({ id: 'app_page_app_deactivate_title' });

  const activateDeactivateConfirmText = isDeactivated
    ? formatMessage(
        { id: 'app_page_confirm_activate_text' },
        { appName: application?.name }
      )
    : formatMessage(
        { id: 'app_page_confirm_deactivate_text' },
        { appName: application?.name }
      );

  const activateDeactivateLabelConfirm = isDeactivated
    ? formatMessage({ id: 'app_page_app_activate_header_title' })
    : formatMessage({ id: 'app_page_app_deactivate_header_title' });

  const activateDeactivateLabelComplection =
    !isDeactivated || currentStep !== 2
      ? formatMessage({ id: 'app_page_app_activated_title' })
      : formatMessage({ id: 'app_page_app_deactivated_title' });

  const activatedDeactivatedComplectionText = isDeactivated
    ? formatMessage({ id: 'deactivated_completion_message' })
    : formatMessage({ id: 'activated_completion_message' });

  useEffect(() => {
    if (isCreated) {
      setModalContent({
        header: formatMessage({ id: 'app_page_app_register_title' }),
        text: formatMessage(
          { id: 'app_page_confirm_register_modal_text' },
          { appName: application?.name }
        ),
        confirmButton: formatMessage({ id: 'app_page_app_register_title' }),
      });
    }
    if (isRegistered) {
      setModalContent({
        header: formatMessage(
          { id: 'app_page_app_submit_to_publish_header_title' },
          { appName: application?.name }
        ),
        text: formatMessage(
          { id: 'app_page_confirm_readytopublish_modal_text' },
          { appName: application?.name }
        ),
        confirmButton: formatMessage({ id: 'app_page_app_submit_to_publish_title' }),
      });
    }
    if (isPublished) {
      setModalContent({
        header: activateDeactivateLabelConfirm,
        text: activateDeactivateConfirmText,
        confirmButton: activateDeactivateLabel,
      });
    }
    if (isPublished && currentStep === 2) {
      setModalContent({
        header: activateDeactivateLabelComplection,
        text: activatedDeactivatedComplectionText,
        confirmButton: activateDeactivateLabel,
      });
    }
  }, [application.status, currentStep]);

  const headerButtonOnClickHandler = () => {
    onOpenModalHandler();
  };

  useEffect(() => {
    if (loaded) {
      setApplication(data);
    }
    if (isRegisterLoaded) {
      setApplication(registerApplicationResponse);
    }

    if (submittingloaded) {
      setApplication(submitToPublishResponse);
    }
    if (activateDeactivateStatusLoaded) {
  
      setApplication(activateDeactivateApplicationResponse);

    }
  }, [
    data,
    activateDeactivateApplicationResponse,
    activateDeactivateStatusLoaded,
    registerApplicationResponse,
    submittingloaded,
    loaded
  ]);

  const rightHeaderSection = useHeaderMenu({
    profileUrl: ROUTE_DEVELOPER_DASHBOARD_MY_PROFILE,
    iconClassName: classes.menu_icon,
  });

  const onOpenModalHandler = () => {
    setCurrentStep(1);
    setIsModalOpen(true);
  };
  const onCloseModalHandler = () => {
    setIsModalOpen(false);
  };
  const [isApplicationInEditMode, setIsApplicationInEditMode] = useState({
    documentationTab: false,
    techDetailsTab: false,
    descriptionTab: false,
  });

  const onChangeIsApplicationInEditMode = (
    tab: string,
    isEditable: boolean
  ): any => {
    setIsApplicationInEditMode((prev) => ({
      ...prev,
      [tab]: isEditable,
    }));
  };
  onChangeIsApplicationInEditMode.DOCUMENTATION_TAB = 'documentationTab';
  onChangeIsApplicationInEditMode.TECH_DETAILS_TAB = 'techDetailsTab';
  onChangeIsApplicationInEditMode.DESCRIPTION_TAB = 'descriptionTab';

  const [isHeaderButtonDisabled, setIsHeaderButtonDisabled] = useState(false);

  useEffect(() => {
    if (
      isApplicationInEditMode.descriptionTab ||
      isApplicationInEditMode.documentationTab ||
      isApplicationInEditMode.techDetailsTab ||
      isReadyToPublish
    ) {
      setIsHeaderButtonDisabled(true);
    } else {
      setIsHeaderButtonDisabled(false);
    }
  }, [isApplicationInEditMode]);

  const onConfirmButtonHandler =async (data?:any) => {
    if (isCreated) {
      registerApplication(application.id);
    }

    if (isRegistered) {
      const response =await submitToPublish(application.id); 
        if (response?.data?.status) {
          setIsHeaderButtonDisabled(true);
          toast.current.show({
            severity: "success",
            sticky:true,
            detail: formatMessage({ id: 'app_page_app_submit_to_publish_success' }),
          });
         }
    }
    if (isDeactivated) {
      activateApplication(id);
    }

    if (!isDeactivated && isPublished) {
      deactivateApplication(id);
    }
  };

  useEffect(() => {
    setCurrentStep(2);
  }, [activateDeactivateStatusLoaded, isDeactivated, isPublished]);

  useEffect(() => {
    if (
      registerApplicationResponse?.errors ||
      registerApplicationResponse?.id
    ) {
      setIsModalOpen(false);
    }
  }, [registerApplicationResponse]);

  useEffect(() => {
    if (
      updatedStatusLoading ||
      !activateDeactivateStatusLoaded ||
      submitToPublishResponse
    ) {
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
    }
    if (submittingloaded && !activateDeactivateStatusLoaded) {
      setIsModalOpen(false);
    }
  }, [
    updatedStatusLoading,
    submittingloaded,
    activateDeactivateStatusLoaded,
    submitToPublishResponse,
  ]);

  return (
    <CommonLayout
      leftSection={
        <Title
          link={formatMessage({ id: 'app_page_apps_link' })}
          text={formatMessage({ id: 'app_page_app_details_title' })}
        />
      }
      logo={
        <Logo
          subLogo={formatMessage({ id: 'developer_landing_header_sub_logo' })}
        />
      }
      leftHeaderSection={<NavBar options={headerNavigation} />}
      rightHeaderSection={rightHeaderSection}
      rightSection={
        loaded && (
          <Button
            disabled={isHeaderButtonDisabled}
            onClick={headerButtonOnClickHandler}
          >
            {headerButtonLabel}
          </Button>
        )
      }
    >
      <div className="card flex justify-content-center">
      <Toast ref={toast} position="top-center"/>      
      </div>
      <div className={classes.top_sections}>
    <AppStatus setApplication={setApplication} application={application} />
      </div>
      <div className={classes.bottom_section}>
        <AppDetails
          applicationId={application.id}
          isPublished={isPublished}
          isReadyToPublish={isReadyToPublish}
          isCreated={isCreated}
          onChangeIsApplicationInEditMode={onChangeIsApplicationInEditMode}
          registerApplicationResponse={registerApplicationResponse}
          submitToPublishResponse={submitToPublishResponse}
          activateDeactivateApplicationResponse={
            activateDeactivateApplicationResponse
          }
          application={application}
        />
      </div>

      {application.name ? (
        <Dialog
          visible={isModalOpen}
          width="600px"
          height="200px"
          onAccept={currentStep !== 2 ? onConfirmButtonHandler : undefined}
          header={modalContent.header}
          previousLabel={''}
          onReject={onCloseModalHandler}
          acceptLabel={modalContent.confirmButton}
          rejectLabel={formatMessage({ id: 'app_page_app_cancel_title' })}
        >
          {currentStep === 1 && (
            <ConfirmModal confirmText={modalContent.text} />
          )}
          {currentStep === 2 && activateDeactivateStatusLoaded && (
            <Completion complectionText={modalContent.text} />
          )}
        </Dialog>
      ) : null}
    </CommonLayout>
  );
};
